import { colorGray } from '@atlas-frontend/design-tokens';
import {
  ClassifiersDataSource,
  cleanArray,
  PermissionCode,
  Table,
  TableOperationButton,
  TableProps,
  TextEllipsis,
  useHasPermission,
} from '@atlas-frontend/sdk';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Flex } from 'antd';
import { useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ClassifierDirectoryTable.translations.json';
import * as Icon from 'react-feather';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';

const { addTranslations, useTranslation } = i18nNsFactory('classifier-table');
addTranslations(translations);

const columnHelper = createColumnHelper<ClassifiersDataSource>();

export interface ClassifierDirectoryTableProps
  extends Omit<
    TableProps<ClassifiersDataSource>,
    'name' | 'columns' | 'scrollContainer' | 'virtual'
  > {
  onCreate: () => void;
}

export const ClassifierDirectoryTable = ({
  onCreate,
  ...rest
}: ClassifierDirectoryTableProps) => {
  const { t } = useTranslation();

  const hasPermissionToManage = useHasPermission(
    PermissionCode.CLASSIFIER_MANAGE,
  );

  const columns: ColumnDef<ClassifiersDataSource, any>[] = useMemo(
    () =>
      cleanArray([
        columnHelper.accessor('code', {
          header: t('column.code'),
          cell: (props) => (
            <TextEllipsis>
              <UiLink
                id={`classifier-${props.row.index}`}
                path={path.classifierView}
                params={{ id: props.row.original.id }}
              >
                {props.renderValue()}
              </UiLink>
            </TextEllipsis>
          ),
          enableColumnFilter: true,
          meta: {
            className: 'sticky-left',
            width: '25ch',
          },
        }),
        columnHelper.accessor('name', {
          header: t('column.name'),
          meta: {
            width: '60%',
            minWidth: 150,
          },
        }),
        columnHelper.accessor('description', {
          header: t('column.description'),
          meta: {
            width: '40%',
            minWidth: 150,
          },
        }),
        columnHelper.accessor('categoryCount', {
          header: t('column.categoryCount'),
          enableColumnFilter: false,
          meta: {
            width: 120,
            textAlign: 'right',
          },
        }),
        columnHelper.accessor('groupCount', {
          header: t('column.groupCount'),
          enableColumnFilter: false,
          meta: {
            width: 90,
            textAlign: 'right',
          },
        }),
        columnHelper.accessor('validPeriod', {
          header: t('column.validPeriod'),
          filterFn: 'validPeriod',
          meta: {
            width: 180,
          },
        }),
        columnHelper.accessor('inUse', {
          header: t('column.inUse.label'),
          cell: (props) =>
            props.cell.getValue()
              ? t('column.inUse.value.yes')
              : t('column.inUse.value.no'),
          enableColumnFilter: true,
          filterFn: 'includesOption',
          meta: {
            width: 130,
          },
        }),
        hasPermissionToManage &&
          columnHelper.display({
            id: 'operations',
            header: () => (
              <Flex justify="center" gap="small">
                <TableOperationButton
                  id="create-classifier"
                  icon={<Icon.Plus size={22} color={colorGray[7]} />}
                  onClick={() => onCreate()}
                  title={t('button.add-classifier')}
                />
              </Flex>
            ),
            enableSorting: false,
            enableColumnFilter: false,
            meta: {
              width: 50,
              className: 'sticky-right',
            },
          }),
      ]),
    [onCreate, t, hasPermissionToManage],
  );

  const options = useMemo(
    () => ({
      inUse: [
        { id: true, name: t('column.inUse.value.yes') },
        { id: false, name: t('column.inUse.value.no') },
      ],
    }),
    [t],
  );

  return (
    <Table
      name="classifier-directory-table"
      columns={columns}
      hideClearFilters
      manualFiltering
      manualSorting
      options={options}
      scrollContainerSelector="#classifier-directory-content"
      sticky
      virtual
      {...rest}
    />
  );
};
